import {useCallback} from 'react';
import {t} from '../../../../../../common/getTranslations';
import {requestContext} from '../../../../../../common/requestContext';
import {twMerge} from '../../../../../../common/twMerge';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {ViewerSetting} from '../../../../../../design-system/organisms/ViewerSetting';
import {ViewerSettingReadType} from '../../../../../../design-system/organisms/ViewerSetting/ViewerSettingReadTypeProps.interface';
import {ViewerSettingSpaceColorType} from '../../../../../../design-system/organisms/ViewerSetting/ViewerSettingSpaceColorItemType.interface';
import {
  ReadType,
  ViewDirection,
} from '../../../../../../graphql/generated/types';
import {
  LOCAL_STORAGE_VIEWER_SETTING_IS_TWO_PAGE_LAYOUT,
  LOCAL_STORAGE_VIEWER_SETTING_READ_TYPE_KEY,
  LOCAL_STORAGE_VIEWER_SETTING_SPACE_COLOR_KEY,
  ViewerType,
} from '../constant/viewerConstant';
import {
  changeReadType,
  changeSpaceColor,
  toggleTwoPageLayout,
} from '../state/imageViewerSlice';
import {setCurrentViewerGuide} from '../state/viewerInfoSlice';
import {ImageViewerReadType} from '../type/ImageViewerReadType';
import {convertViewerReadTypeToGuideType} from '../util/convertViewerReadTypeToGuideType';
import {saveViewerSettingToLocalStorage} from '../util/saveViewerSettingToLocalStorage';

export function ImageViewerSettingContainer({
  productId,
  ...props
}: {
  productId: number;
}) {
  const {isMobile} = requestContext.requestInfo.display;
  const dispatch = useTypedDispatch();

  const {
    imageViewerReadType,
    imageViewerSpaceColor,
    imageViewerIsTwoPageLayout,
    isScrollOnlyImageViewer,
    isReverseViewDirection,
  } = useTypedSelector(state => ({
    imageViewerReadType: state.imageViewer.settings.readType,
    imageViewerSpaceColor: state.imageViewer.settings.spaceColor,
    imageViewerIsTwoPageLayout: state.imageViewer.settings.isTwoPageLayout,
    isScrollOnlyImageViewer:
      state.imageViewer.viewData[productId]?.downloadData?.readType ===
      ReadType.Scroll, // 스크롤만 가능한 회차의 경우 서버에서 주는 ReadType이 Scroll
    isReverseViewDirection:
      state.imageViewer.viewData[productId]?.downloadData?.viewDirection ===
      ViewDirection.Backward,
  }));

  const onClickReadType = useCallback(
    (clickedReadType: ViewerSettingReadType) => {
      // 스크롤만 가능한 회차일 경우 클릭 이벤트 무시
      if (isScrollOnlyImageViewer) return;

      const clickedImageViewerReadType =
        clickedReadType === 'Type1'
          ? ImageViewerReadType.Page
          : ImageViewerReadType.Scroll;

      saveViewerSettingToLocalStorage(
        ViewerType.ImageViewer,
        LOCAL_STORAGE_VIEWER_SETTING_READ_TYPE_KEY,
        clickedImageViewerReadType
      );
      dispatch(changeReadType(clickedImageViewerReadType));
      dispatch(
        setCurrentViewerGuide(
          convertViewerReadTypeToGuideType({
            viewerType: ViewerType.ImageViewer,
            viewerReadType: clickedImageViewerReadType,
            isReverseViewDirection,
          })
        )
      );
    },
    [dispatch, isReverseViewDirection, isScrollOnlyImageViewer]
  );

  const onClickSpaceColor = useCallback(
    (clickedItem: ViewerSettingSpaceColorType) => {
      saveViewerSettingToLocalStorage(
        ViewerType.ImageViewer,
        LOCAL_STORAGE_VIEWER_SETTING_SPACE_COLOR_KEY,
        clickedItem
      );
      dispatch(changeSpaceColor(clickedItem));
    },
    [dispatch]
  );

  const onClickTwoLayoutToggle = useCallback(() => {
    saveViewerSettingToLocalStorage(
      ViewerType.ImageViewer,
      LOCAL_STORAGE_VIEWER_SETTING_IS_TWO_PAGE_LAYOUT,
      (!imageViewerIsTwoPageLayout).toString()
    );
    dispatch(toggleTwoPageLayout());
  }, [dispatch, imageViewerIsTwoPageLayout]);

  return (
    <ViewerSetting {...props}>
      <ViewerSetting.ReadType
        isMobile={isMobile}
        readType1={t('viewer_setting_mode_page_button')}
        readType2={t('viewer_setting_mode_scroll_button')}
        isReadType1Disabled={isScrollOnlyImageViewer} // 스크롤 회차일 경우 페이지 버튼을 disable
        currentReadType={
          imageViewerReadType === ImageViewerReadType.Page ? 'Type1' : 'Type2'
        }
        onClickReadType={onClickReadType}
        className={twMerge('px-24pxr', !isMobile && 'h-40pxr')}
      />
      <ViewerSetting.Divider isMobile={isMobile} />
      <ViewerSetting.SpaceColor
        isMobile={isMobile}
        currentSpaceColor={imageViewerSpaceColor}
        onClickSpaceColor={onClickSpaceColor}
        className="px-24pxr"
      />
      {!isMobile && (
        <div>
          <ViewerSetting.Divider isMobile={isMobile} />
          <ViewerSetting.TwoPageLayout
            isMobile={isMobile}
            isDisabled={imageViewerReadType === ImageViewerReadType.Scroll}
            isTwoPageLayout={imageViewerIsTwoPageLayout}
            onClickTwoLayoutToggle={onClickTwoLayoutToggle}
            className={twMerge('px-24pxr', !isMobile && 'h-40pxr')}
          />
        </div>
      )}
    </ViewerSetting>
  );
}
